// biome-ignore-all lint/suspicious/noConsole: the logger writes to the console
import { localStorageInterface } from '../interfaces/localStorageInterface';

const LogLevels = Object.freeze({
  DEBUG: 0,
  LOG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
  FATAL: 5,
  OFF: 6,
} as const);
export type LogLevel = (typeof LogLevels)[keyof typeof LogLevels];

export interface Logger {
  readonly name: string;
  /** Wins over the configured level when set. */
  loggingLevelOverride: number | undefined;
  debug(...args: unknown[]): void;
  log(...args: unknown[]): void;
  info(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
  fatal(...args: unknown[]): void;
  loggingLevel(): number;
}

const isLevelInRange = (level: number) => level >= LogLevels.DEBUG && level <= LogLevels.OFF;

class LoggerConfigurator {
  /** The remote flag value; ignored unless it is a number in the level range. */
  featureFlagLoggingLevel: unknown = null;
  readonly LogLevelsEnum = LogLevels;

  retrieveLoggingLevel = (): number => {
    let loggingLevel: number = process.env.NODE_ENV !== 'test' ? this.LogLevelsEnum.INFO : this.LogLevelsEnum.OFF; // default for prod

    if (typeof this.featureFlagLoggingLevel === 'number') {
      if (isLevelInRange(this.featureFlagLoggingLevel)) {
        loggingLevel = this.featureFlagLoggingLevel;
      }
    }

    const loggingLevelLocal = localStorageInterface.getItem('loggingLevel');
    const localLoggingLevelNumber = loggingLevelLocal ? Number(loggingLevelLocal) : undefined;
    if (localLoggingLevelNumber !== undefined && isLevelInRange(localLoggingLevelNumber)) {
      loggingLevel = localLoggingLevelNumber;
    }

    return loggingLevel;
  };

  storeLoggingLevel = (loggingLevel: LogLevel): void => {
    localStorageInterface.setItem('loggingLevel', String(loggingLevel));
  };

  setFeatureFlagLoggingLevel = (level: unknown): void => {
    this.featureFlagLoggingLevel = level;
  };
}

const loggerMethods = {
  loggingLevelOverride: undefined as number | undefined,
  debug(this: Logger, ...args: unknown[]) {
    if (this.loggingLevel() <= configurator.LogLevelsEnum.DEBUG) {
      const message = (args && args.shift()) || '';
      console.debug(`%c${this.name} DEBUG:  ${message}`, 'color: green;', ...args);
    }
  },
  log(this: Logger, ...args: unknown[]) {
    if (this.loggingLevel() <= configurator.LogLevelsEnum.LOG) {
      const message = (args && args.shift()) || '';
      console.log(`%c${this.name} LOG: ${message}`, 'color: navy;', ...args);
    }
  },
  info(this: Logger, ...args: unknown[]) {
    if (this.loggingLevel() <= configurator.LogLevelsEnum.INFO) {
      const message = (args && args.shift()) || '';
      console.info(`%c${this.name} INFO:  ${message}`, 'color: blue;', ...args);
    }
  },
  warn(this: Logger, ...args: unknown[]) {
    if (this.loggingLevel() <= configurator.LogLevelsEnum.WARN) {
      const message = (args && args.shift()) || '';
      console.warn(`%c${this.name} WARN:  ${message}`, 'color: crimson;', ...args);
    }
  },
  error(this: Logger, ...args: unknown[]) {
    if (this.loggingLevel() <= configurator.LogLevelsEnum.ERROR) {
      const message = (args && args.shift()) || '';
      console.error(`%c${this.name} ERROR:  ${message}`, 'color: red;', ...args);
    }
  },
  fatal(this: Logger, ...args: unknown[]) {
    if (this.loggingLevel() <= configurator.LogLevelsEnum.FATAL) {
      const message = (args && args.shift()) || '';
      console.error(`%c${this.name} FATAL:  ${message}`, 'color: darkred;', ...args);
    }
  },
  loggingLevel(this: Logger) {
    return this.loggingLevelOverride !== undefined ? this.loggingLevelOverride : configurator.retrieveLoggingLevel();
  },
};

export const configurator = new LoggerConfigurator();
export function getLogger(name?: string): Logger {
  return { ...loggerMethods, name: name || 'logger' };
}
